import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getMaintenanceStrategiesByWorkspaceId,
  getMaintenanceStrategy,
  type ListMaintenanceStrategiesByWorkspaceIdProps,
} from "@/api/resources/maintenanceStrategies/api";
import { transformPaginatedMaintenanceStrategies } from "@/api/resources/maintenanceStrategies/selectors";
import i18n from "@/providers/i18n";

const entity = i18n.t("maintenance-strategy");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("maintenance-strategies");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const maintenanceStrategiesQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "maintenanceStrategies"],
  lists: () => [...maintenanceStrategiesQueryKeys.all(), "list"],
  listByWorkspaceId: (props: ListMaintenanceStrategiesByWorkspaceIdProps) => [
    ...maintenanceStrategiesQueryKeys.lists(),
    "byWorkspaceId",
    props,
  ],
  listActiveByWorkspaceId: (props: ListMaintenanceStrategiesByWorkspaceIdProps) => [
    ...maintenanceStrategiesQueryKeys.lists(),
    "activeByWorkspaceId",
    props,
  ],
  byId: (id?: string) => [...maintenanceStrategiesQueryKeys.all(), "byId", id],
};

export const maintenanceStrategiesQueries = {
  listByWorkspaceId: ({ workspace_id, params }: ListMaintenanceStrategiesByWorkspaceIdProps) =>
    queryOptions({
      queryKey: [...maintenanceStrategiesQueryKeys.listByWorkspaceId({ workspace_id, params })],
      queryFn: workspace_id ? () => getMaintenanceStrategiesByWorkspaceId({ workspace_id, params }) : skipToken,
      meta: {
        errorMessage: entitiesErrorMessage,
      },
      select: ({ data, count }) => ({ count, data: transformPaginatedMaintenanceStrategies(data) }),
    }),
  // all active strategies (no limit), eg. to expand their schedule into virtual calendar events
  listActiveByWorkspaceId: ({ workspace_id, params }: ListMaintenanceStrategiesByWorkspaceIdProps, enabled = true) =>
    queryOptions({
      queryKey: [...maintenanceStrategiesQueryKeys.listActiveByWorkspaceId({ workspace_id, params })],
      queryFn: workspace_id
        ? () => getMaintenanceStrategiesByWorkspaceId({ workspace_id, params: { ...params, is_active: true } })
        : skipToken,
      meta: {
        errorMessage: entitiesErrorMessage,
      },
      select: ({ data }) => data,
      enabled,
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...maintenanceStrategiesQueryKeys.byId(id)],
      queryFn: id ? () => getMaintenanceStrategy(id) : skipToken,
      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
